/**
 * Assumptions & Issues — security assumptions, known
 * vulnerabilities, previous assessments, security considerations.
 * Pulls from `formatted.assumptions_and_issues`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, StringListField, ObjectListField, type SectionProps } from './parts';
import LevelBadge from '../../../../components/ui/LevelBadge';
import { getSeverityColor } from '../../../../utils/severityStyle';

// LevelBadge is a JSX primitive; cast for use from TSX (mirrors the
// *Any convention across the step-components).
const LevelBadgeAny = LevelBadge as unknown as React.ComponentType<any>;

const PATH = ['assumptions_and_issues'];

/** Criticality / severity render as a filled `LevelBadge` swatch (the
 *  cell reads as a rating, not a status). Colour comes from the shared
 *  `athena` severity palette so Critical/High/Medium/Low match Steps 3-7. */
const SeverityBadge = ({ value }: { value: unknown }) => {
  if (value == null || value === '') return null;
  return <LevelBadgeAny color={getSeverityColor(String(value), { scheme: 'athena' })} label={String(value)} size="sm" />;
};

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

export default function AssumptionsIssues({ data, isEditing, onChange }: SectionProps) {
  const ai = data ?? {};
  const assumptions = ai.security_assumptions?.assumptions ?? [];
  const vulns = ai.known_vulnerabilities?.vulnerabilities ?? [];
  const prevAssess = ai.previous_assessments?.assessments ?? [];
  const considerations = ai.security_considerations ?? [];

  const assumptionColumns = [
    { key: 'assumption_id', label: 'ID' },
    { key: 'assumption_description', label: 'Assumption' },
    { key: 'related_components', label: 'Components', render: joinList },
    { key: 'criticality', label: 'Criticality', render: (v: unknown) => <SeverityBadge value={v} /> },
    { key: 'impact_if_violated', label: 'Impact if Violated' },
    { key: 'validation_method', label: 'Validation' },
  ];
  const vulnColumns = [
    { key: 'cve_id', label: 'CVE' },
    { key: 'description', label: 'Description' },
    { key: 'affected_components', label: 'Affected', render: joinList },
    { key: 'severity', label: 'Severity', render: (v: unknown) => <SeverityBadge value={v} /> },
    { key: 'cvss_score', label: 'CVSS' },
    { key: 'mitigation_status', label: 'Status' },
    { key: 'mitigation_measures', label: 'Mitigations', render: joinList },
    { key: 'discovery_date', label: 'Discovered' },
  ];
  const assessColumns = [
    { key: 'assessment_type', label: 'Type' },
    { key: 'assessment_date', label: 'Date' },
    { key: 'assessor', label: 'Assessor' },
    { key: 'key_findings', label: 'Key Findings', render: joinList },
    { key: 'remediation_status', label: 'Remediation' },
    { key: 'report_reference', label: 'Reference' },
  ];

  return (
    <SectionCard title="Assumptions & Issues" id="assumptions_and_issues">
      <SubSection title="Security assumptions" count={assumptions.length}>
        <ObjectListField columns={assumptionColumns} rows={assumptions} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_assumptions', 'assumptions']} />
      </SubSection>

      <SubSection title="Known vulnerabilities" count={vulns.length}>
        <ObjectListField columns={vulnColumns} rows={vulns} isEditing={isEditing} onChange={onChange} path={[...PATH, 'known_vulnerabilities', 'vulnerabilities']} />
      </SubSection>

      <SubSection title="Previous assessments" count={prevAssess.length}>
        <ObjectListField columns={assessColumns} rows={prevAssess} isEditing={isEditing} onChange={onChange} path={[...PATH, 'previous_assessments', 'assessments']} />
      </SubSection>

      <SubSection title="Security considerations">
        <StringListField label="Considerations" items={considerations} isEditing={isEditing} onChange={onChange} path={[...PATH, 'security_considerations']} />
      </SubSection>
    </SectionCard>
  );
}
